import { ActivityType } from '../service/ActivityFeedStorageItem';
import {
    ActivityFeedItem,
    LikeActivityFeedItem,
    MentionActivityFeedItem,
    UserAtMentionedActivityFeedItem,
    FlightReservationActivityFeedItem,
    ParcelDeliveryActivityFeedItem,
} from '../service/ActivityFeedItem';
import {
    activityFeedV2LikeCardBody,
    activityFeedV2TwoOrMoreLikesCardBody,
    activityFeedV2MentionCardBody,
    activityFeedV2UserAtMentionedCardBody,
    activityFeedV2FlightReservationCardBody,
    activityFeedV2FlightHeroActionText,
    activityFeedV2ParcelDeliveryCardBody,
    activityFeedV2ParcelHeroActionText,
} from './activityFeedCard.locstring.json';
import { ActivityFeedActionType, OpenLinkActionType } from '../components/ActivityFeedActions';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import { PersonaCardBehaviorProps } from 'owa-persona/lib/components/PersonaCardBehavior';
import encode from 'owa-encoding-utils/lib/utils/html/encode';
import { formatMonthDayShortTime, asOwaDateInUserTz } from 'owa-datetime';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import tryCreateEntityItem from 'txp-data/lib/utils/data/tryCreateEntityItem';
import FlightReservation from 'txp-data/lib/schema/FlightReservation';
import ParcelDelivery from 'txp-data/lib/schema/ParcelDelivery';
import { getTaskInsightFeedCard } from './MyAnalyticsFeedCardPresentationDetails';
export interface ActivityFeedCardDetailsProps {
    item: ActivityFeedCardDetails;
    onAction: (itemId: string, action: ActivityFeedActionType) => void;
    itemId: string;
}

export interface ActivityFeedCardDetails {
    senderInfo: PersonaCardBehaviorProps[];
    messageId: string;
    cardTitle: string;
    cardSubTitle?: string;
    cardIcon: string;
    isSeen: boolean;
    timeStamp: Date;
    cardType: ActivityCardLayoutType;
    activityType: ActivityType;
    heroButtonProperties?: HeroActionProperties[];
    cardOnClickAction?: ActivityFeedActionType;
    dismissAction?(): void;
}

export interface HeroActionProperties {
    buttonTitle: string;
    buttonActionUrl?: string;
    buttonActionServiceConfig?: HeroActionServiceCallConfiguration;
}

export interface HeroActionServiceCallConfiguration {
    url: string;
    method: string;
    body?: string;
}

export enum ActivityCardLayoutType {
    SingleTitleActivityCard = 0,
    MultiTitleActivityCard = 1,
    HeroActionCard = 2,
}

export function getActivityCardPresentationDetails(item: ActivityFeedItem) {
    switch (item.type) {
        case ActivityType.Like:
            return getLikeFeedCard(item);
        case ActivityType.AtMention:
            return getAtMentionFeedCard(item);
        case ActivityType.UserAtMentioned:
            return getUserAtMentionFeedCard(item);
        case ActivityType.TxpFlightReservation:
            return getFlightReservationFeedCard(item);
        case ActivityType.TxpParcelDelivery:
            return getParcelDeliveryFeedCard(item);
        case ActivityType.TaskNotificationInsight:
        case ActivityType.FollowUpNotificationInsight:
            return getTaskInsightFeedCard(item);
    }
    return {} as ActivityFeedCardDetails;
}

function getFlightReservationFeedCard(
    item: FlightReservationActivityFeedItem
): ActivityFeedCardDetails {
    const flightData = tryCreateEntityItem(item.payload) as FlightReservation;
    if (flightData) {
        if (item.type === ActivityType.TxpFlightReservation) {
            const departureTime = new Date(flightData.departureTime);

            // Only show card if flight departs within 24 hours.
            // Later this logic will be updated using data from TEE.
            const dateThreshold = new Date();
            dateThreshold.setDate(dateThreshold.getDate() + 1);

            if (!!departureTime && departureTime > dateThreshold) {
                return {} as ActivityFeedCardDetails;
            }
        }

        const departureTimestamp = formatMonthDayShortTime(
            asOwaDateInUserTz(new Date(flightData.departureTime))
        );

        const titleText = format(
            loc(activityFeedV2FlightReservationCardBody),
            formatBoldMarkdownString(departureTimestamp)
        );
        return {
            senderInfo: [{ name: flightData.airline?.name, emailAddress: '' }],
            messageId: flightData.originalEmailInternetMessageId,
            cardTitle: titleText,
            timeStamp: item.timestamp,
            isSeen: item.isSeen,
            cardType: ActivityCardLayoutType.SingleTitleActivityCard,
            activityType: ActivityType.TxpFlightReservation,
            cardIcon: ControlIcons.AirplaneSolid,
            heroButtonProperties: [
                {
                    buttonTitle: loc(activityFeedV2FlightHeroActionText),
                    buttonActionUrl: flightData.checkinUrl,
                } as HeroActionProperties,
            ],
        } as ActivityFeedCardDetails;
    } else {
        return {} as ActivityFeedCardDetails;
    }
}

function getParcelDeliveryFeedCard(item: ParcelDeliveryActivityFeedItem): ActivityFeedCardDetails {
    const parcelData = tryCreateEntityItem(item.payload) as ParcelDelivery;
    if (parcelData) {
        const deliveryTimestamp = formatMonthDayShortTime(
            asOwaDateInUserTz(new Date(parcelData.expectedArrivalUntil))
        );
        const titleText = format(
            loc(activityFeedV2ParcelDeliveryCardBody),
            formatBoldMarkdownString(deliveryTimestamp)
        );
        return {
            senderInfo: [{ name: parcelData.provider?.name, emailAddress: '' }],
            messageId: parcelData.originalEmailInternetMessageId,
            cardTitle: titleText,
            timeStamp: item.timestamp,
            isSeen: item.isSeen,
            cardType: ActivityCardLayoutType.SingleTitleActivityCard,
            activityType: ActivityType.TxpParcelDelivery,
            cardIcon: ControlIcons.DeliveryTruck,
            heroButtonProperties: [
                {
                    buttonTitle: loc(activityFeedV2ParcelHeroActionText),
                    buttonActionUrl: parcelData.trackingUrl,
                } as HeroActionProperties,
            ],
        } as ActivityFeedCardDetails;
    } else {
        return {} as ActivityFeedCardDetails;
    }
}

function getLikeFeedCard(item: LikeActivityFeedItem): ActivityFeedCardDetails {
    let titleText = '';
    const encodedSubject = encode(item.payload.TargetSubject);
    if (item.payload.Actors.length == 1) {
        titleText = format(
            loc(activityFeedV2LikeCardBody),
            item.payload.Actors[0].EmailAddress.Name,
            encodedSubject
        );
    } else {
        titleText = format(
            loc(activityFeedV2TwoOrMoreLikesCardBody),
            item.payload.Actors.length.toString(),
            encodedSubject
        );
    }
    var senderInfo = item.payload.Actors.map(function (actor) {
        return { name: actor.EmailAddress.Name, emailAddress: actor.EmailAddress.EmailAddress };
    });
    return {
        senderInfo: senderInfo,
        messageId: item.payload.TargetLogicalId,
        cardTitle: titleText,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.Like,
        cardIcon: ControlIcons.Like,
    } as ActivityFeedCardDetails;
}

function getAtMentionFeedCard(item: MentionActivityFeedItem): ActivityFeedCardDetails {
    const encodedSubject = encode(item.payload.Subject);
    const cardTitle = format(
        loc(activityFeedV2MentionCardBody),
        formatBoldMarkdownString(item.payload.Actor.EmailAddress?.Name),
        encodedSubject
    );
    return {
        senderInfo: [
            {
                name: item.payload.Actor.EmailAddress?.Name,
                emailAddress: item.payload.Actor.EmailAddress?.EmailAddress,
            },
        ],
        messageId: item.payload.InternetMessageId,
        cardTitle: cardTitle,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.MultiTitleActivityCard,
        activityType: ActivityType.AtMention,
        cardIcon: ControlIcons.Accounts,
    } as ActivityFeedCardDetails;
}

function formatBoldMarkdownString(text: string): any {
    let trimmedText = text.trim();
    if (trimmedText == '') {
        return trimmedText;
    } else {
        return `**${trimmedText}**`;
    }
}

function getUserAtMentionFeedCard(item: UserAtMentionedActivityFeedItem): ActivityFeedCardDetails {
    const encodedFilename = encode(item.payload.SourceFileName);
    const cardTitle = format(
        loc(activityFeedV2UserAtMentionedCardBody),
        getFirstName(item.payload.Mentioner.DisplayName),
        encodedFilename
    );

    const fileIcon = {
        ...getFileTypeIconProps({
            extension: item.payload.SourceFileExtension,
            size: 16,
            imageFileType: 'png',
        }),
    };

    const cardOnClickAction = {
        actionName: 'Action.OpenUrl',
        url: item.payload.ObjectId + '?web=1',
        itemId: item.id,
        messageId: item.payload.ObjectId,
    } as OpenLinkActionType;
    return {
        senderInfo: [
            {
                name: item.payload.Mentioner.DisplayName,
                emailAddress: item.payload.Mentioner.Email,
            },
        ],
        messageId: item.payload.ObjectId,
        cardTitle: cardTitle,
        timeStamp: item.timestamp,
        isSeen: item.isSeen,
        cardType: ActivityCardLayoutType.SingleTitleActivityCard,
        activityType: ActivityType.UserAtMentioned,
        cardIcon: fileIcon.iconName,
        cardOnClickAction: cardOnClickAction,
    } as ActivityFeedCardDetails;
}

function getFirstName(displayName: String): String {
    return displayName.split(' ')[0];
}
